import { useCallback, useMemo } from 'react'
import { useUserInfoContext } from 'Contexts/UserInfoContext'
import { firstOfFollowingMonth, sameDay } from 'Utils/helpers/dateUtils'
import { useUserProgramContext } from 'Contexts/UserProgramContext'
import { isActiveProgram } from 'Utils/types/benefitProgram'
import isBefore from 'date-fns/isBefore'
import isAfter from 'date-fns/isAfter'
import { PurchasesTableRow } from 'Utils/types/purchase'

export enum EarliestDateType {
  Membership = 'MEMBERSHIP',
  Invoice = 'INVOICE',
  GracePeriod = 'GRACE_PERIOD'
}

/*  Gets the earliest date a purchase can be reported for.
 *  The first day of the most recent uninvoiced month is preferable, but
 *  that doesn't exist if there are no invoices yet.
 *  In that case we use the beginning of the user's membership */
export const useEarliestReportableDate = (programId?: string) => {
  const {
    state: { latestInvoiceDetails }
  } = useUserInfoContext()

  const {
    state: { programs }
  } = useUserProgramContext()

  const selectedProgram = useMemo(
    () => programs.find((program) => program.id.toString() === programId),
    [programs, programId]
  )

  const earliestReportableProgramDate = useMemo(() => {
    const membershipStartDate = selectedProgram?.membership.scheduledStartDate

    if (selectedProgram?.inGracePeriod) {
      const floor = selectedProgram.gracePeriodStartDate ?? membershipStartDate
      if (floor) return { date: floor, type: EarliestDateType.GracePeriod }
    }

    if (membershipStartDate && !latestInvoiceDetails)
      return {
        date: membershipStartDate,
        type: EarliestDateType.Membership
      }

    if (membershipStartDate && latestInvoiceDetails) {
      if (isBefore(membershipStartDate, latestInvoiceDetails.dateCreated)) {
        return {
          date: firstOfFollowingMonth(latestInvoiceDetails.dateCreated),
          type: EarliestDateType.Invoice
        }
      } else {
        return { date: membershipStartDate, type: EarliestDateType.Membership }
      }
    }

    return undefined
  }, [latestInvoiceDetails, selectedProgram])

  const latestReportableProgramDate = useMemo(() => {
    if (selectedProgram?.inGracePeriod) return selectedProgram.gracePeriodEndDate
    return undefined
  }, [selectedProgram])

  const reportable = useCallback(
    (purchase: Pick<PurchasesTableRow, 'date'>) => {
      return programs.filter(isActiveProgram).some((program) => {
        const membershipStart = program.membership.scheduledStartDate
        const afterMembershipStart =
          sameDay(purchase.date, membershipStart) || isAfter(purchase.date, membershipStart)
        if (!afterMembershipStart) return false

        if (program.inGracePeriod && program.gracePeriodEndDate) {
          return purchase.date <= program.gracePeriodEndDate
        }

        return !latestInvoiceDetails || isAfter(purchase.date, latestInvoiceDetails.dateCreated)
      })
    },
    [latestInvoiceDetails, programs]
  )

  return {
    earliestReportableProgramDate,
    latestReportableProgramDate,
    reportable
  }
}
